import {
  activityVehicleAssociationTransformer,
  deletedRoutesTransformer,
  editRouteAPITransformer,
  inputActivitiesAPITransformer,
  inputActivityWithoutRouteAPITransformer,
  parsedActivitiesSpreadsheetRowAPITransformer,
  routingRoutingSettingsAPITransformer,
} from './ApiTransformers/RoutingAPITransformers';
import {
  type ActivitiesSpreadsheetImportFormValues,
  type ActivitiesSummary,
  type ActivitiesSummaryAPI,
  activitiesSummaryAttributes,
  activityAttributes,
  type ActivityCompletionStatusFilter,
  type ActivityRouteRelation,
  type ActivityVehicleAssociation,
  type DeleteRoutes,
  type DeleteRoutesAPI,
  type EditRoute,
  type InputActivity,
  type InputActivityAPI,
  type NewActivity,
  type NewActivityAPI,
  NewActivityType,
  type NewRoute,
  type NewRouteAPI,
  type NewRouteReport,
  type NewRouteReportAPI,
  type OperationSummary,
  type OperationSummaryAPI,
  operationSummaryAttributes,
  pagedActivitiesAttributes,
  pagedRoutesAttributes,
  routeAttributes,
  routeReportAttributes,
  type RoutingSettings,
  type SpreadsheetImportResultAddressAPI,
  spreadsheetImportResultAddressAttributes,
} from './Definitions';
import {
  activitiesPageableTransformer,
  activityTransformer,
} from './Tranformers/ActivitiesTransformers';
import {
  activitiesSummaryTransformer,
  operationSummaryTransformer,
  routePageTransformer,
  routeReportTransformer,
  routeTransformer,
  spreadsheetImportResultAddressTransformer,
} from './Tranformers/RoutingTransformers';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from './utils';
import { gql } from '@apollo/client';

import type {
  DateRange,
  PageableContent,
  PageableContentAPI,
  Sort,
  UUID,
} from '@cobliteam/common-types';

import graphQLRequester from '../../request/GraphQLRequester';

export class GraphQLRoutingRequester {
  public getRoute = (id: UUID, hasPOCPermission = false): Promise<NewRoute> =>
    graphQLRequester
      .query<{ route: NewRouteAPI }>({
        query: gql`
      query GetRoute($id: ID!) {
        route(input: $id) {
          ${routeAttributes({ withPoints: true, withPOC: hasPOCPermission })}
        }
      }
    `,
        variables: { id },
      })
      .then((result) => routeTransformer(result.route));

  public getOperationSummary = ({
    period,
    devicesIds,
    driversIds,
    textFilter,
    vehiclesIds,
    activityRouteRelation,
  }: {
    period: DateRange;
    textFilter?: string;
    devicesIds?: string[];
    driversIds?: UUID[];
    vehiclesIds?: UUID[];
    activityRouteRelation?: ActivityRouteRelation;
  }): Promise<OperationSummary> =>
    graphQLRequester
      .query<{ operationSummary: OperationSummaryAPI }>({
        query: gql`
          query GetOperationSummary($input: OperationSummaryInput!) {
            operationSummary(input: $input) {
              ${operationSummaryAttributes}
            }
          }
        `,
        variables: {
          input: {
            period,
            devices_ids: devicesIds,
            drivers_ids: driversIds,
            text_filter: textFilter,
            vehicles_ids: vehiclesIds,
            activity_route_relation: activityRouteRelation,
          },
        },
      })
      .then((result) => operationSummaryTransformer(result.operationSummary));

  public getActivitiesOperationSummary = ({
    period,
    devicesIds,
    driversIds,
    vehiclesIds,
  }: {
    period: DateRange;
    devicesIds?: string[];
    driversIds?: UUID[];
    vehiclesIds?: UUID[];
  }): Promise<ActivitiesSummary> =>
    graphQLRequester
      .query<{ activitySummary: ActivitiesSummaryAPI }>({
        query: gql`
          query GetActivityOperationSummary($input: ActivitiesSummaryInput!) {
            activitySummary(input: $input) {
              ${activitiesSummaryAttributes}
            }
          }
        `,
        variables: {
          input: {
            start_time: period.start,
            end_time: period.end,
            devices_ids: devicesIds,
            drivers_ids: driversIds,
            vehicles_ids: vehiclesIds,
          },
        },
      })
      .then((result) => activitiesSummaryTransformer(result.activitySummary));

  public createRoutes = (
    routingSettings: RoutingSettings,
    hasPOCPermission = false
  ): Promise<NewRoute[]> =>
    graphQLRequester
      .mutate<{ createRoutes: NewRouteAPI[] }>({
        mutation: gql`
          mutation RoutingSettings($routeSettings: RoutingSettings) {
            createRoutes(input: $routeSettings) {
              ${routeAttributes({
                withPoints: true,
                withPOC: hasPOCPermission,
              })}
            }
          }
        `,
        errorPolicy: 'none',
        variables: {
          routeSettings: routingRoutingSettingsAPITransformer(routingSettings),
        },
      })
      .then((result) => result.createRoutes.map(routeTransformer));

  public createActivities = (activities: InputActivity[]): Promise<any> =>
    graphQLRequester
      .mutate<{ createActivities: InputActivityAPI[] }>({
        mutation: gql`
          mutation createActivities($activitiesToCreate: [InputActivity!]!) {
            createActivities(input: $activitiesToCreate) {
              ${activityAttributes({ withPOC: false })}
            }
          }
        `,
        variables: {
          activitiesToCreate: inputActivitiesAPITransformer(activities),
        },
      })
      .then((result) => result.createActivities);

  public deleteActivities = (ids: UUID[]): Promise<UUID[]> =>
    graphQLRequester
      .mutate<{ deleteActivities: UUID[] }>({
        mutation: gql`
          mutation DeleteActivities($activitiesToDelete: DeleteActivitiesIds!) {
            deleteActivities(input: $activitiesToDelete)
          }
        `,
        variables: { activitiesToDelete: { ids } },
      })
      .then((result) => result.deleteActivities);

  public updateActivity = (activity: InputActivity): Promise<NewActivity> =>
    graphQLRequester
      .mutate<{ updateActivity: NewActivityAPI }>({
        mutation: gql`
          mutation updateActivity($activityToUpdate: ActivityUpdate!) {
            updateActivity(input: $activityToUpdate) {
              ${activityAttributes({ withPOC: false })}
            }
          }
        `,
        variables: {
          activityToUpdate: {
            id: activity.id,
            type: NewActivityType.STOP,
            input_activity: inputActivityWithoutRouteAPITransformer(activity),
          },
        },
      })
      .then((result) => activityTransformer(result.updateActivity));

  public getRoutes = (
    period: DateRange,
    page: number,
    size: number,
    sort?: Sort,
    textFilter?: string,
    vehiclesIds?: UUID[],
    driversIds?: UUID[],
    hasPOCPermission = false
  ): Promise<PageableContent<NewRoute>> =>
    graphQLRequester
      .query<{ routesV2: PageableContentAPI<NewRouteAPI> }>({
        query: gql`
      query PaginatedRoutes($listRoutesQueryParams: ListRoutesInputV2!) {
        routesV2(input: $listRoutesQueryParams) {
          ${pagedRoutesAttributes({
            withPoints: false,
            withPOC: hasPOCPermission,
          })}
        }
      }
    `,
        variables: {
          listRoutesQueryParams: {
            period,
            page,
            size,
            sort,
            text_filter: textFilter,
            vehicles_ids: vehiclesIds,
            drivers_ids: driversIds,
          },
        },
      })
      .then((result) => routePageTransformer(result.routesV2));

  public getTrackingRoutes = (
    period: DateRange,
    vehicleId?: UUID,
    hasPOCPermission = false
  ): Promise<NewRoute[]> =>
    graphQLRequester
      .query<{ routes: PageableContentAPI<NewRouteAPI> }>({
        query: gql`
      query PaginatedRoutes($listRoutesQueryParams: ListRoutesInput) {
        routes(input: $listRoutesQueryParams) {
          ${pagedRoutesAttributes({
            withPoints: true,
            withPOC: hasPOCPermission,
          })}
        }
      }
    `,
        variables: {
          listRoutesQueryParams: {
            period,
            page: DEFAULT_PAGE,
            size: DEFAULT_PAGE_SIZE,
            vehicle_id: vehicleId,
          },
        },
      })
      .then((result) => result.routes.content.map(routeTransformer));

  public editRoute = (
    route: EditRoute,
    hasPOCPermission = false
  ): Promise<NewRoute> =>
    graphQLRequester
      .mutate<{ updateRouteV2: NewRouteAPI }>({
        mutation: gql`
          mutation RouteUpdate($routeToUpdate: RouteUpdate) {
            updateRouteV2(input: $routeToUpdate) {
              ${routeAttributes({
                withPoints: true,
                withPOC: hasPOCPermission,
              })}
            }
          }
        `,
        variables: { routeToUpdate: editRouteAPITransformer(route) },
      })
      .then((result) => routeTransformer(result.updateRouteV2));

  public deleteRoutes = async (routeToDelete: DeleteRoutes): Promise<UUID[]> =>
    graphQLRequester
      .mutate<{ deleteRoutes: DeleteRoutesAPI }>({
        mutation: gql`
          mutation DeleteRoutes($routesToDelete: DeleteRoutesInput) {
            deleteRoutes(input: $routesToDelete)
          }
        `,
        variables: {
          routesToDelete: deletedRoutesTransformer(routeToDelete),
        },
      })
      .then((result) => result.deleteRoutes.ids);

  public importSpreadsheet = async (
    input: ActivitiesSpreadsheetImportFormValues
  ): Promise<InputActivity[]> =>
    graphQLRequester
      .mutate<{ importSpreadsheet: SpreadsheetImportResultAddressAPI[] }>({
        mutation: gql`
          mutation ImportSpreadsheet($input: [SpreadsheetRow!]!) {
            importSpreadsheet(input: $input) {
              ${spreadsheetImportResultAddressAttributes}
            }
          }
        `,
        variables: {
          input: parsedActivitiesSpreadsheetRowAPITransformer(input),
        },
      })
      .then((result) =>
        result.importSpreadsheet.map(spreadsheetImportResultAddressTransformer)
      );

  public requestRoutesReport = async (
    input: DateRange,
    hasPOCPermission = false,
    hasAnalyticsAndReportPermission = false
  ): Promise<NewRouteReport[]> =>
    graphQLRequester
      .query<{ routesReport: NewRouteReportAPI[] }>({
        query: gql`
      query RoutesReport($input: DateRange!) {
        routesReport(input: $input) {
          ${routeReportAttributes({
            withObservations: hasPOCPermission,
            withExecutedDistance: hasAnalyticsAndReportPermission,
          })}
        }
      }
    `,
        variables: { input },
      })
      .then((result) => result.routesReport.map(routeReportTransformer));

  public getActivities = (
    period: DateRange,
    page: number,
    size: number,
    sort?: Sort,
    textFilter?: string,
    vehicleIds?: UUID[],
    driverIds?: UUID[],
    completionStatuses?: ActivityCompletionStatusFilter[],
    hasPOCPermission = false
  ): Promise<PageableContent<NewActivity>> =>
    graphQLRequester
      .query<{ activities: PageableContentAPI<NewActivityAPI> }>({
        query: gql`
        query GetActivities($listActivitiesQueryParams: ListActivitiesInput!) {
          activities(input: $listActivitiesQueryParams) {
            ${pagedActivitiesAttributes({ withPOC: hasPOCPermission })}
          }
        }
      `,
        variables: {
          listActivitiesQueryParams: {
            page,
            size,
            sort,
            start_in_millis: period.start,
            end_in_millis: period.end,
            text_filter: textFilter,
            vehicles_ids: vehicleIds,
            drivers_ids: driverIds,
            completion_statuses: completionStatuses,
            activities_type: NewActivityType.STOP,
          },
        },
      })
      .then((result) => activitiesPageableTransformer(result.activities));

  public associateVehiclesToActivities = (
    vehicleAssociations: ActivityVehicleAssociation[],
    hasPOCPermission = false
  ): Promise<NewActivity[]> =>
    graphQLRequester
      .mutate<{ vehicleAssociations: NewActivityAPI[] }>({
        mutation: gql`
            mutation ActivityVehicleAssociation($vehicleAssociations: [ActivityVehicleAssociationInput!]!) {
              vehicleAssociations(input: $vehicleAssociations) {
                ${activityAttributes({ withPOC: hasPOCPermission })}
              }
            }
          `,
        variables: {
          vehicleAssociations: vehicleAssociations.map(
            activityVehicleAssociationTransformer
          ),
        },
      })
      .then((result) => result.vehicleAssociations.map(activityTransformer));
}

export default GraphQLRoutingRequester;
